import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import {
  Loader2,
  Package,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  CreditCard,
  Pill,
  Calendar,
  Truck,
  Plus,
} from 'lucide-react';
import { useGuidedOrders } from '@/hooks/useGuidedOrders';
import { GuidedOrderDetailSheet } from '@/components/guided-order/GuidedOrderDetailSheet';
import {
  GuidedOrderStatusBadge,
  GuidedPaymentStatusBadge,
} from '@/components/guided-order/GuidedOrderStatusBadge';
import { OrderLifecycleTracker } from '@/components/guided-order/OrderLifecycleTracker';
import { subscriptionService, type Subscription } from '@/services/subscriptionService';
import type { GuidedOrder } from '@/types/guidedOrder';
import { GuidedIntakeModal } from '@/components/clinic/GuidedIntakeModal';
import { useClinic } from '@/contexts/ClinicContext';
import type { Patient } from '@/lib/types';

interface PatientGuidedOrdersTabProps {
  patientId?: string | null;
  clinicId?: string;
  // When viewing an external patient (cross-clinic via external_patients),
  // restrict the listing to Nimbus-fulfilled orders only.
  fulfillmentSource?: 'clinic' | 'nimbus';
  patient?: Patient | null;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

function formatDollars(dollars: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(dollars);
}

function formatDate(d: string | Date | null | undefined): string {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function resolvePatientEmail(patient?: Patient | null): string | undefined {
  if (!patient) return undefined;
  const contacts = (patient as Patient & { contacts?: Array<{ key?: string; value?: string; is_default?: boolean; is_deleted?: boolean }> }).contacts;
  if (Array.isArray(contacts)) {
    const emails = contacts.filter(c => c.key === 'email' && c.value && !c.is_deleted);
    const preferred = emails.find(c => c.is_default) || emails[0];
    if (preferred?.value) return preferred.value;
  }
  return patient.email || undefined;
}

function formatFrequency(freq: string): string {
  const map: Record<string, string> = {
    weekly: 'Weekly',
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    yearly: 'Yearly',
  };
  return map[freq] || freq;
}

const SUB_STATUS_STYLES: Record<string, string> = {
  active: 'bg-green-50 text-green-700 border-green-300',
  paused: 'bg-amber-50 text-amber-700 border-amber-300',
  pending_amendment: 'bg-orange-50 text-orange-700 border-orange-300',
  cancelled: 'bg-red-50 text-red-700 border-red-300',
  expired: 'bg-slate-50 text-slate-500 border-slate-300',
};

// ─── One-Time Orders Sub-Tab ────────────────────────────────────────────────

function OneTimeOrdersTable({
  patientId,
  clinicId,
  fulfillmentSource,
}: {
  patientId: string;
  clinicId?: string;
  fulfillmentSource?: 'clinic' | 'nimbus';
}) {
  const [offset, setOffset] = useState(0);
  const [detailOrderId, setDetailOrderId] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const LIMIT = 20;

  const { data, isLoading, error } = useGuidedOrders({
    clinic_id: clinicId ?? '',
    patient_id: patientId,
    is_subscription: 'false',
    ...(fulfillmentSource ? { fulfillment_source: fulfillmentSource } : {}),
    limit: LIMIT,
    offset,
  });

  const orders = data?.data ?? [];
  const total = data?.meta?.total ?? 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700">
        Failed to load guided orders.
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-slate-400">
        <Package className="h-10 w-10 mb-3" />
        <p className="text-sm font-medium">No guided orders</p>
        <p className="text-xs">This patient has no one-time guided orders.</p>
      </div>
    );
  }

  return (
    <>
      <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px] text-sm">
            <thead style={{ backgroundColor: 'rgb(245, 247, 247)' }}>
              <tr className="border-b border-slate-200">
                <th
                  className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700"
                  style={{ fontSize: '0.7rem' }}
                >
                  Order ID
                </th>
                <th
                  className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700"
                  style={{ fontSize: '0.7rem' }}
                >
                  Program
                </th>
                <th
                  className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700"
                  style={{ fontSize: '0.7rem' }}
                >
                  Amount
                </th>
                <th
                  className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700"
                  style={{ fontSize: '0.7rem' }}
                >
                  Status
                </th>
                <th
                  className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700"
                  style={{ fontSize: '0.7rem' }}
                >
                  Payment
                </th>
                <th
                  className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700"
                  style={{ fontSize: '0.7rem' }}
                >
                  Lifecycle
                </th>
                <th
                  className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700"
                  style={{ fontSize: '0.7rem' }}
                >
                  Date
                </th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order: GuidedOrder) => {
                const meta = order.metadata as Record<string, unknown> | undefined;
                const hasAmendment = order.amendments?.some(a => a.status === 'pending');
                return (
                  <tr
                    key={order.id}
                    className="border-b border-slate-100 transition-colors hover:bg-slate-50/80 cursor-pointer"
                    onClick={() => {
                      setDetailOrderId(order.id);
                      setDetailOpen(true);
                    }}
                  >
                    <td className="px-4 py-3" style={{ fontSize: '0.8rem' }}>
                      <span className="font-mono text-xs font-medium text-blue-600">
                        {order.order_number != null ? `GO-${order.order_number}` : '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3" style={{ fontSize: '0.8rem' }}>
                      <div className="flex items-center gap-2 font-medium text-slate-900">
                        {(meta?.program_label as string) || order.clinicProgramOption?.label || 'Guided Order'}
                        {hasAmendment && (
                          <AlertTriangle className="h-3.5 w-3.5 text-orange-500" />
                        )}
                        {order.clinicProgramOption?.is_subscription && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-blue-50 text-blue-600 border-blue-200">
                            Recurring
                          </Badge>
                        )}
                      </div>
                      {order.subscription && (
                        <div className="text-[11px] text-slate-400 mt-0.5">
                          Subscription: {order.subscription.status} &middot; {formatFrequency(order.subscription.frequency)}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-900" style={{ fontSize: '0.8rem' }}>
                      {formatCurrency(order.amount_cents)}
                    </td>
                    <td className="px-4 py-3" style={{ fontSize: '0.8rem' }}>
                      <GuidedOrderStatusBadge
                        status={order.status}
                        hasPendingCancellation={order.cancellationRequests?.some((cr: any) => cr.status === 'pending')}
                      />
                    </td>
                    <td className="px-4 py-3" style={{ fontSize: '0.8rem' }}>
                      <GuidedPaymentStatusBadge paymentStatus={order.payment_status} />
                    </td>
                    <td className="px-4 py-3" style={{ fontSize: '0.8rem' }}>
                      <OrderLifecycleTracker
                        status={order.status}
                        paymentStatus={order.payment_status}
                        compact
                      />
                    </td>
                    <td className="px-4 py-3 text-slate-500" style={{ fontSize: '0.8rem' }}>
                      {formatDate(order.created_at)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {total > LIMIT && (
        <div className="flex items-center justify-between mt-4 text-sm text-slate-500">
          <span>
            Showing {offset + 1}–{Math.min(offset + LIMIT, total)} of {total}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - LIMIT))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={offset + LIMIT >= total}
              onClick={() => setOffset(offset + LIMIT)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <GuidedOrderDetailSheet
        orderId={detailOrderId}
        open={detailOpen}
        onOpenChange={setDetailOpen}
      />
    </>
  );
}

// ─── Recurring Subscriptions Sub-Tab ────────────────────────────────────────

function SubscriptionRow({ sub }: { sub: Subscription }) {
  const [expanded, setExpanded] = useState(false);
  const statusStyle = SUB_STATUS_STYLES[sub.status] || SUB_STATUS_STYLES.expired;
  const isPendingAmendment = sub.status === 'pending_amendment';

  return (
    <>
      <tr
        className="border-b border-slate-100 transition-colors hover:bg-slate-50/80 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <td className="w-10 px-4 py-3">
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-slate-400" />
          ) : (
            <ChevronRight className="h-4 w-4 text-slate-400" />
          )}
        </td>
        <td className="px-4 py-3" style={{ fontSize: '0.8rem' }}>
          <div className="flex items-center gap-2 font-medium text-slate-900">
            {sub.program?.title || 'Subscription'}
            {isPendingAmendment && (
              <AlertTriangle className="h-3.5 w-3.5 text-orange-500" />
            )}
          </div>
        </td>
        <td className="px-4 py-3 font-medium text-slate-900" style={{ fontSize: '0.8rem' }}>
          {formatDollars(sub.amount)}
        </td>
        <td className="px-4 py-3 text-slate-700" style={{ fontSize: '0.8rem' }}>
          {formatFrequency(sub.frequency)}
        </td>
        <td className="px-4 py-3" style={{ fontSize: '0.8rem' }}>
          <Badge variant="outline" className={statusStyle}>
            {isPendingAmendment ? 'Pending Amendment' : sub.status.charAt(0).toUpperCase() + sub.status.slice(1)}
          </Badge>
        </td>
        <td className="px-4 py-3 text-slate-500" style={{ fontSize: '0.8rem' }}>
          {formatDate(sub.next_run_date)}
        </td>
        <td className="px-4 py-3 text-slate-500" style={{ fontSize: '0.8rem' }}>
          {sub.last_payment_date ? formatDate(sub.last_payment_date) : '—'}
        </td>
      </tr>

      {/* Expanded detail */}
      {expanded && (
        <tr className="bg-gradient-to-b from-slate-50 to-white">
          <td colSpan={7} className="p-5">
            <div className="space-y-4">
              {/* Section cards row */}
              <div className="grid grid-cols-2 gap-4">
                {/* Medications Card */}
                <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                  <h4 className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1.5 mb-3">
                    <Pill className="h-3.5 w-3.5" />
                    Medications
                  </h4>
                  {sub.subscriptionMedications && sub.subscriptionMedications.length > 0 ? (
                    <ul className="space-y-2">
                      {sub.subscriptionMedications.map(med => (
                        <li key={med.id} className="flex items-start justify-between gap-2 text-xs rounded-md bg-slate-50 px-2.5 py-2">
                          <span className="min-w-0 truncate text-slate-700">{med.medicationRequest?.medication || 'Unknown'}</span>
                          <span className="text-slate-400 whitespace-nowrap shrink-0 font-mono text-[11px]">
                            Qty: {med.medicationRequest?.quantity_authorized ?? '—'}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-slate-400 text-xs">No medications linked</p>
                  )}
                </div>

                {/* Details Card */}
                <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                  <h4 className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1.5 mb-3">
                    <RefreshCw className="h-3.5 w-3.5" />
                    Details
                  </h4>
                  <div className="space-y-2.5 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Auto-renew</span>
                      <span className={`font-medium ${sub.auto_renew ? 'text-emerald-600' : 'text-slate-400'}`}>{sub.auto_renew ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Created</span>
                      <span className="text-slate-700">{formatDate(sub.created_at)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Amendment info */}
              {isPendingAmendment && (
                <div className="rounded-lg border border-orange-200 bg-orange-50/80 p-3 flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-orange-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium text-orange-700 text-xs">Price Amendment Pending</p>
                    <p className="text-orange-600 text-xs mt-0.5">
                      Subscription paused — awaiting patient consent for price increase.
                    </p>
                  </div>
                </div>
              )}

              {/* Payment History Accordion */}
              <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
                <Accordion type="single" collapsible>
                  <AccordionItem value="payment-history" className="border-0">
                    <AccordionTrigger className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-400 hover:no-underline hover:bg-slate-50/50">
                      <span className="flex items-center gap-1.5">
                        <CreditCard className="h-3.5 w-3.5" />
                        Payment History
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4 pt-0 text-xs">
                      <div className="space-y-2.5 max-w-md">
                        <div className="flex items-center justify-between">
                          <span className="text-slate-500">Last payment</span>
                          <span className={`font-medium ${sub.last_payment_status === 'success' ? 'text-emerald-600' : 'text-slate-700'}`}>{sub.last_payment_status || '—'}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-slate-500">Created</span>
                          <span className="text-slate-700">{formatDate(sub.created_at)}</span>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>

              {/* Subscription ID */}
              <p className="text-[11px] text-slate-400 font-mono">
                ID: {sub.id}
              </p>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function RecurringGuidedOrderRow({
  order,
  linkedSubscription,
}: {
  order: GuidedOrder;
  linkedSubscription?: Subscription;
}) {
  const [expanded, setExpanded] = useState(false);
  const meta = order.metadata as Record<string, unknown> | undefined;
  const hasAmendment = order.amendments?.some(a => a.status === 'pending');
  const freq = order.clinicProgramOption?.subscription_frequency || (meta?.subscription_frequency as string) || 'monthly';
  const isLinked = !!order.subscription;
  const sub = linkedSubscription;
  const isPendingAmendment = sub?.status === 'pending_amendment';

  return (
    <>
      <tr
        className={`border-b border-slate-100 transition-colors hover:bg-slate-50/80 ${isLinked ? 'cursor-pointer' : ''}`}
        onClick={() => isLinked && setExpanded(!expanded)}
      >
        <td className="w-10 px-4 py-3">
          {isLinked ? (
            expanded ? (
              <ChevronDown className="h-4 w-4 text-slate-400" />
            ) : (
              <ChevronRight className="h-4 w-4 text-slate-400" />
            )
          ) : (
            <span className="inline-block w-4" />
          )}
        </td>
        <td className="px-4 py-3" style={{ fontSize: '0.8rem' }}>
          <span className="font-mono text-xs font-medium text-blue-600">
            GO-{order.order_number}
          </span>
        </td>
        <td className="px-4 py-3" style={{ fontSize: '0.8rem' }}>
          <div className="flex items-center gap-2 font-medium text-slate-900">
            {(meta?.program_label as string) || order.clinicProgramOption?.label || 'Guided Order'}
            {hasAmendment && <AlertTriangle className="h-3.5 w-3.5 text-orange-500" />}
          </div>
        </td>
        <td className="px-4 py-3 font-medium text-slate-900" style={{ fontSize: '0.8rem' }}>
          {formatCurrency(order.amount_cents)}
        </td>
        <td className="px-4 py-3 text-slate-700" style={{ fontSize: '0.8rem' }}>
          {formatFrequency(freq)}
        </td>
        <td className="px-4 py-3" style={{ fontSize: '0.8rem' }}>
          <GuidedOrderStatusBadge
            status={order.status}
            hasPendingCancellation={order.cancellationRequests?.some((cr: any) => cr.status === 'pending')}
          />
        </td>
        <td className="px-4 py-3" style={{ fontSize: '0.8rem' }}>
          <GuidedPaymentStatusBadge paymentStatus={order.payment_status} />
        </td>
        <td className="px-4 py-3" style={{ fontSize: '0.8rem' }}>
          {isLinked ? (
            <Badge
              className="inline-flex w-fit items-center gap-1 border-0 bg-emerald-600 px-2 py-0.5 font-medium text-white hover:bg-emerald-600"
              style={{ fontSize: '0.7rem' }}
            >
              Linked
            </Badge>
          ) : (
            <Badge
              className="inline-flex w-fit items-center gap-1 border-0 bg-amber-500 px-2 py-0.5 font-medium text-white hover:bg-amber-500"
              style={{ fontSize: '0.7rem' }}
            >
              Pending
            </Badge>
          )}
        </td>
        <td className="px-4 py-3 text-slate-500" style={{ fontSize: '0.8rem' }}>
          {formatDate(order.created_at)}
        </td>
      </tr>

      {/* Expanded subscription detail nested under the guided order */}
      {expanded && sub && (
        <tr className="bg-gradient-to-b from-slate-50 to-white">
          <td colSpan={9} className="p-5">
            <div className="space-y-4">
              {/* Section cards row */}
              <div className="grid grid-cols-3 gap-4">
                {/* Subscription Card */}
                <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                  <h4 className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1.5 mb-3">
                    <RefreshCw className="h-3.5 w-3.5" />
                    Subscription
                  </h4>
                  <div className="space-y-2.5 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Status</span>
                      <Badge
                        variant="outline"
                        className={SUB_STATUS_STYLES[sub.status] || SUB_STATUS_STYLES.expired}
                      >
                        {isPendingAmendment ? 'Pending Amendment' : sub.status.charAt(0).toUpperCase() + sub.status.slice(1)}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Amount</span>
                      <span className="font-medium text-slate-800">{formatDollars(sub.amount)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Frequency</span>
                      <span className="font-medium text-slate-700">{formatFrequency(sub.frequency)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Next Run</span>
                      <span className="text-slate-700">{formatDate(sub.next_run_date)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Auto-renew</span>
                      <span className={`text-xs font-medium ${sub.auto_renew ? 'text-emerald-600' : 'text-slate-400'}`}>{sub.auto_renew ? 'Yes' : 'No'}</span>
                    </div>
                  </div>
                </div>

                {/* Medications Card */}
                <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                  <h4 className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1.5 mb-3">
                    <Pill className="h-3.5 w-3.5" />
                    Medications
                  </h4>
                  {sub.subscriptionMedications && sub.subscriptionMedications.length > 0 ? (
                    <ul className="space-y-2">
                      {sub.subscriptionMedications.map(med => (
                        <li key={med.id} className="flex items-start justify-between gap-2 text-xs rounded-md bg-slate-50 px-2.5 py-2">
                          <span className="min-w-0 truncate text-slate-700">{med.medicationRequest?.medication || 'Unknown'}</span>
                          <span className="text-slate-400 whitespace-nowrap shrink-0 font-mono text-[11px]">
                            Qty: {med.medicationRequest?.quantity_authorized ?? '—'}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-slate-400 text-xs">No medications linked</p>
                  )}
                </div>

                {/* Shipping Card */}
                <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                  <h4 className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1.5 mb-3">
                    <Truck className="h-3.5 w-3.5" />
                    Shipping
                  </h4>
                  <div className="space-y-2.5 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Carrier</span>
                      <span className="font-medium text-slate-700">{sub.prescription?.shipping_carrier_name || '—'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Price</span>
                      <span className="font-medium text-slate-700">
                        {sub.prescription?.is_free_shipment
                          ? 'Free'
                          : sub.prescription?.shipping_cost_cents != null
                            ? formatCurrency(sub.prescription.shipping_cost_cents)
                            : '—'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {isPendingAmendment && (
                <div className="rounded-lg border border-orange-200 bg-orange-50/80 p-3 flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-orange-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium text-orange-700 text-xs">Price Amendment Pending</p>
                    <p className="text-orange-600 text-xs mt-0.5">
                      Subscription paused — awaiting patient consent for price increase.
                    </p>
                  </div>
                </div>
              )}

              {/* Payment History Accordion */}
              <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
                <Accordion type="single" collapsible>
                  <AccordionItem value="payment-history" className="border-0">
                    <AccordionTrigger className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-400 hover:no-underline hover:bg-slate-50/50">
                      <span className="flex items-center gap-1.5">
                        <CreditCard className="h-3.5 w-3.5" />
                        Payment History
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4 pt-0 text-xs">
                      <div className="space-y-2.5 max-w-md">
                        <div className="flex items-center justify-between">
                          <span className="text-slate-500">Last payment</span>
                          <span className={`font-medium ${sub.last_payment_status === 'success' ? 'text-emerald-600' : 'text-slate-700'}`}>{sub.last_payment_status || '—'}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-slate-500">Last payment date</span>
                          <span className="text-slate-700">{sub.last_payment_date ? formatDate(sub.last_payment_date) : '—'}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-slate-500">Created</span>
                          <span className="text-slate-700">{formatDate(sub.created_at)}</span>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>

              {/* Subscription ID */}
              <p className="text-[11px] text-slate-400 font-mono">
                Subscription ID: {sub.id}
              </p>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function RecurringSubscriptionsTable({
  patientId,
  clinicId,
  fulfillmentSource,
}: {
  patientId: string;
  clinicId?: string;
  fulfillmentSource?: 'clinic' | 'nimbus';
}) {
  const { data: subData, isLoading: subLoading, isFetching: subFetching, error: subError, refetch } = useQuery({
    queryKey: ['patient-subscriptions', patientId],
    queryFn: () => subscriptionService.getPatientSubscriptions(patientId, 1, 50),
    enabled: !!patientId,
  });

  const { data: orderData, isLoading: orderLoading, isFetching: orderFetching, refetch: refetchOrders } = useGuidedOrders({
    clinic_id: clinicId ?? '',
    patient_id: patientId,
    is_subscription: 'true',
    ...(fulfillmentSource ? { fulfillment_source: fulfillmentSource } : {}),
    limit: 50,
    offset: 0,
  });

  const subscriptions = subData?.subscriptions ?? [];
  const recurringOrders = orderData?.data ?? [];
  const isLoading = subLoading || orderLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (subError) {
    return (
      <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700">
        Failed to load subscriptions.
      </div>
    );
  }

  if (subscriptions.length === 0 && recurringOrders.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-slate-400">
        <RefreshCw className="h-10 w-10 mb-3" />
        <p className="text-sm font-medium">No recurring subscriptions</p>
        <p className="text-xs">This patient has no active or past subscriptions.</p>
      </div>
    );
  }

  // Build a map of subscription_id → full Subscription for quick lookup
  const subscriptionMap = new Map<string, Subscription>();
  subscriptions.forEach(sub => subscriptionMap.set(sub.id, sub));

  // Find subscription IDs that are linked to guided orders
  const linkedSubIds = new Set<string>();
  recurringOrders.forEach((order: GuidedOrder) => {
    if (order.subscription?.id) linkedSubIds.add(order.subscription.id);
  });

  // Orphan subscriptions = not linked to any guided order
  const orphanSubscriptions = subscriptions.filter(sub => !linkedSubIds.has(sub.id));

  return (
    <div className="space-y-5">
      {/* Recurring Guided Orders (with linked subscriptions nested inline) */}
      {recurringOrders.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 text-xs text-slate-500">
              <h4 className="font-medium text-slate-600">
                Recurring Guided Orders ({recurringOrders.length})
              </h4>
              <span className="flex items-center gap-1">
                <span className="inline-block h-2 w-2 rounded-full bg-green-500" />
                {recurringOrders.filter((o: GuidedOrder) => o.subscription).length} linked
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              disabled={subFetching || orderFetching}
              onClick={() => {
                refetch();
                refetchOrders();
              }}
            >
              <RefreshCw
                className={`h-3 w-3 mr-1 ${subFetching || orderFetching ? 'animate-spin' : ''}`}
              />
              {subFetching || orderFetching ? 'Refreshing…' : 'Refresh'}
            </Button>
          </div>
          <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1100px] text-sm">
                <thead style={{ backgroundColor: 'rgb(245, 247, 247)' }}>
                  <tr className="border-b border-slate-200">
                    <th
                      className="w-10 px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700"
                      style={{ fontSize: '0.7rem' }}
                    />
                    <th
                      className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700"
                      style={{ fontSize: '0.7rem' }}
                    >
                      Order ID
                    </th>
                    <th
                      className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700"
                      style={{ fontSize: '0.7rem' }}
                    >
                      Program
                    </th>
                    <th
                      className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700"
                      style={{ fontSize: '0.7rem' }}
                    >
                      Amount
                    </th>
                    <th
                      className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700"
                      style={{ fontSize: '0.7rem' }}
                    >
                      Frequency
                    </th>
                    <th
                      className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700"
                      style={{ fontSize: '0.7rem' }}
                    >
                      Status
                    </th>
                    <th
                      className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700"
                      style={{ fontSize: '0.7rem' }}
                    >
                      Payment
                    </th>
                    <th
                      className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700"
                      style={{ fontSize: '0.7rem' }}
                    >
                      Subscription
                    </th>
                    <th
                      className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700"
                      style={{ fontSize: '0.7rem' }}
                    >
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {recurringOrders.map((order: GuidedOrder) => (
                    <RecurringGuidedOrderRow
                      key={order.id}
                      order={order}
                      linkedSubscription={
                        order.subscription?.id
                          ? subscriptionMap.get(order.subscription.id)
                          : undefined
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Orphan Subscriptions — not linked to any guided order */}
      {orphanSubscriptions.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 text-xs text-slate-500">
              <h4 className="font-medium text-slate-600">
                Other Subscriptions ({orphanSubscriptions.length})
              </h4>
              <span className="flex items-center gap-1">
                <span className="inline-block h-2 w-2 rounded-full bg-green-500" />
                {orphanSubscriptions.filter(s => s.status === 'active').length} active
              </span>
            </div>
          </div>

          <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-sm">
                <thead style={{ backgroundColor: 'rgb(245, 247, 247)' }}>
                  <tr className="border-b border-slate-200">
                    <th
                      className="w-10 px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700"
                      style={{ fontSize: '0.7rem' }}
                    />
                    <th
                      className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700"
                      style={{ fontSize: '0.7rem' }}
                    >
                      Program
                    </th>
                    <th
                      className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700"
                      style={{ fontSize: '0.7rem' }}
                    >
                      Amount
                    </th>
                    <th
                      className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700"
                      style={{ fontSize: '0.7rem' }}
                    >
                      Frequency
                    </th>
                    <th
                      className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700"
                      style={{ fontSize: '0.7rem' }}
                    >
                      Status
                    </th>
                    <th
                      className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700"
                      style={{ fontSize: '0.7rem' }}
                    >
                      Next Run
                    </th>
                    <th
                      className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700"
                      style={{ fontSize: '0.7rem' }}
                    >
                      Last Payment
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {orphanSubscriptions.map(sub => (
                    <SubscriptionRow key={sub.id} sub={sub} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Tab Component ─────────────────────────────────────────────────────

export default function PatientGuidedOrdersTab({ patientId, clinicId, fulfillmentSource, patient }: PatientGuidedOrdersTabProps) {
  const queryClient = useQueryClient();
  const { clinic } = useClinic();
  const [enrollOpen, setEnrollOpen] = useState(false);

  if (!patientId) {
    return (
      <div className="text-sm text-slate-400 py-8 text-center">
        Patient not loaded.
      </div>
    );
  }

  const isGuided = clinic?.clinic_archetype === 'guided';
  const isHybrid = clinic?.clinic_archetype === 'hybrid';
  const canEnroll =
    (isGuided || isHybrid) &&
    !!clinic?.id &&
    !!patientId &&
    patient?.status !== 'inactive';

  return (
    <Tabs defaultValue="one-time" className="w-full">
      <div className="flex items-center justify-between gap-4">
        <TabsList className="bg-slate-100/80 p-1 rounded-lg">
          <TabsTrigger
            value="one-time"
            className="rounded-md px-4 py-1.5 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm"
          >
            <Package className="mr-2 h-4 w-4" />
            One-Time Orders
          </TabsTrigger>
          <TabsTrigger
            value="recurring"
            className="rounded-md px-4 py-1.5 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Recurring Subscriptions
          </TabsTrigger>
        </TabsList>
        {canEnroll && (
          <Button
            size="sm"
            onClick={() => setEnrollOpen(true)}
            className="gap-2 bg-brand-teal hover:bg-brand-teal/90"
          >
            <Plus className="h-4 w-4" />
            Send Link
          </Button>
        )}
      </div>

      <TabsContent value="one-time" className="mt-4">
        <OneTimeOrdersTable patientId={patientId} clinicId={clinicId} fulfillmentSource={fulfillmentSource} />
      </TabsContent>

      <TabsContent value="recurring" className="mt-4">
        <RecurringSubscriptionsTable patientId={patientId} clinicId={clinicId} fulfillmentSource={fulfillmentSource} />
      </TabsContent>

      {canEnroll && clinic?.id && (
        <GuidedIntakeModal
          open={enrollOpen}
          onOpenChange={setEnrollOpen}
          clinicId={clinic.id}
          patientDbId={patientId}
          patientEmail={resolvePatientEmail(patient)}
          patientName={patient?.full_name}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['guided-orders'] });
            queryClient.invalidateQueries({ queryKey: ['patient-intakes', patientId] });
          }}
        />
      )}
    </Tabs>
  );
}
